import React from "react";

type AnimatedCedeLogoProps = {
  width?: number;
  height?: number;
  shadowColor?: "blue" | "red";
};

export const AnimatedCedeLogo: React.FC<AnimatedCedeLogoProps> = ({
  width = 100,
  height = 100,
  shadowColor = "blue",
}) => (
  <>
    <style key="panda-animations">{`
      .panda-container {
        animation: pandaBreathe 3s ease-in-out infinite, pandaGlow-${shadowColor} 3s ease-in-out infinite;
      }

      .panda-eye {
        animation: pandaBlink 3s ease-in-out infinite;
      }

      .panda-eye-delayed {
        animation: pandaBlink 3s ease-in-out infinite 0.1s;
      }

      @keyframes pandaBreathe {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }

      @keyframes pandaBlink {
        0%, 10% { transform: scaleY(1); }
        15%, 20% { transform: scaleY(0.1); }
        25%, 100% { transform: scaleY(1); }
      }

      @keyframes pandaGlow-blue {
        0%, 100% { filter: drop-shadow(0 0 2px color-mix(in srgb, var(--color-cede-logo-shadow-blue) 40%, transparent)); }
        50% { filter: drop-shadow(0 0 12px color-mix(in srgb, var(--color-cede-logo-shadow-blue) 80%, transparent)); }
      }

      @keyframes pandaGlow-red {
        0%, 100% { filter: drop-shadow(0 0 2px color-mix(in srgb, var(--color-cede-logo-shadow-red) 40%, transparent)); }
        50% { filter: drop-shadow(0 0 12px color-mix(in srgb, var(--color-cede-logo-shadow-red) 80%, transparent)); }
      }
    `}</style>

    <div className="inline-flex items-center justify-center panda-container">
      <svg width={width} height={height} viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0.00244869 9.0023C0.00244869 4.2146 3.88331 0.335938 8.66823 0.335938H27.4453C32.2327 0.335938 36.1111 4.21705 36.1111 9.0023V27.7807C36.1111 32.5684 32.2302 36.447 27.4453 36.447H8.62413C3.86126 36.447 0 32.5855 0 27.8223V8.99986L0.00244869 9.0023Z"
          fill="url(#paint0_linear_8964_90193)"
        />
        <path
          d="M2.08696 10.0853C2.08696 5.8513 5.51946 2.41856 9.75313 2.41856H26.3644C30.5981 2.41856 34.0306 5.8513 34.0306 10.0853V26.6976C34.0306 30.9316 30.5981 34.3643 26.3644 34.3643H9.71393C5.49986 34.3643 2.08451 30.9487 2.08451 26.7344V10.0828L2.08696 10.0853Z"
          fill="black"
        />
        <path
          d="M4.16902 11.1682C4.16902 7.48559 7.15316 4.50124 10.8356 4.50124H25.281C28.9634 4.50124 31.9475 7.48559 31.9475 11.1682V25.6147C31.9475 29.2973 28.9634 32.2817 25.281 32.2817H10.8037C7.14092 32.2817 4.17147 29.312 4.17147 25.6465V11.1682H4.16902Z"
          fill="white"
        />

        {/* Panda ears */}
        <path
          d="M15.0144 19.7775C16.3743 17.4219 15.9852 14.6512 14.1455 13.589C12.3057 12.5267 9.71185 13.5751 8.35196 15.9307C6.99208 18.2862 7.3811 21.0569 9.22088 22.1192C11.0607 23.1814 13.6545 22.133 15.0144 19.7775Z"
          fill="black"
        />
        <path
          d="M26.9719 22.1388C28.8117 21.0765 29.2007 18.3058 27.8409 15.9503C26.481 13.5947 23.8871 12.5463 22.0474 13.6086C20.2076 14.6709 19.8185 17.4415 21.1784 19.7971C22.5383 22.1526 25.1321 23.201 26.9719 22.1388Z"
          fill="black"
        />

        {/* Panda nose/mouth */}
        <path
          d="M18.0961 23.7892C18.0961 23.7892 17.0621 23.7475 16.6922 24.0121C16.3222 24.2768 16.3467 24.6859 16.3467 24.6859C16.3467 24.6859 16.2977 25.1049 16.4202 25.3769C16.5427 25.6489 17.2606 25.6023 17.6134 26.2075C17.6134 26.2075 17.9368 26.3178 18.0961 26.2859"
          fill="black"
        />
        <path
          d="M18.0961 23.7892C18.0961 23.7892 19.13 23.7476 19.5 24.0122C19.8699 24.2768 19.8454 24.686 19.8454 24.686C19.8454 24.686 19.8944 25.105 19.7719 25.377C19.6494 25.6489 18.9316 25.6024 18.5788 26.2076C18.5788 26.2076 18.2553 26.3178 18.0961 26.2859"
          fill="black"
        />

        {/* Animated panda eyes */}
        <g>
          <path
            className="panda-eye"
            d="M13.5944 19.6462C14.3941 19.6462 15.0424 18.9979 15.0424 18.1981C15.0424 17.3984 14.3941 16.7501 13.5944 16.7501C12.7947 16.7501 12.1465 17.3984 12.1465 18.1981C12.1465 18.9979 12.7947 19.6462 13.5944 19.6462Z"
            fill="white"
            style={{ transformOrigin: "13.5944px 18.1981px" }}
          />
        </g>

        <g>
          <path
            className="panda-eye-delayed"
            d="M22.671 19.6682C23.4707 19.6682 24.1189 19.0199 24.1189 18.2202C24.1189 17.4204 23.4707 16.7721 22.671 16.7721C21.8713 16.7721 21.223 17.4204 21.223 18.2202C21.223 19.0199 21.8713 19.6682 22.671 19.6682Z"
            fill="white"
            style={{ transformOrigin: "22.671px 18.2202px" }}
          />
        </g>

        <defs>
          <linearGradient
            id="paint0_linear_8964_90193"
            x1="18.0556"
            y1="0.335937"
            x2="18.0556"
            y2="40.7308"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#5EA6FF" />
            <stop offset="1" stopColor="#5EA6FF" stopOpacity="0.4" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  </>
);
